"use client";

import { Paths } from "@app/routes";
import { ConfirmAction, WithPermission } from "@atlas/composite";
import {
  Badge,
  Button,
  type CellContext,
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  FormSelectField,
  Label,
  type PaginationState,
  ToolbarGroup,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { FilterBuilder, usePermission, useTranslation } from "@atlas/sdk";
import {
  IHubCatalogStatus,
  IHubCatalogVisibility,
  IHubReviewStatus,
} from "@enums";
import type { IHubCatalog, IHubCatalogType, IHubReview } from "@models";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useHubCatalogTypes,
  useSearchHubCatalogs,
  useSearchHubReviews,
  useUpdateHubCatalog,
} from "@query";
import { HUB_PERM } from "@shared";
import { getHubCatalogLifecycleBadge } from "@utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

const DATA_GRID_LAYOUT = {
  columnsVisibility: false,
  headerBackground: true,
  tableBorder: true,
  height: "full",
  headerSticky: true,
} as const;

function formatSubmittedAt(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function canShowCatalogReviewAction(status: IHubCatalogStatus): boolean {
  return status !== IHubCatalogStatus.Draft;
}

const ROW_CLICK_SUPPRESS_MS = 500;

type VisibilityChangeConfirm = {
  catalog: IHubCatalog;
  next: IHubCatalogVisibility;
};

function visibilityLabelForValue(
  value: IHubCatalogVisibility,
  options: { value: IHubCatalogVisibility; label: string }[],
): string {
  return options.find((o) => o.value === value)?.label ?? String(value);
}

function visibilityReadOnlyBadgeVariant(
  v: IHubCatalogVisibility,
): "neutral" | "success" | "info" {
  switch (v) {
    case IHubCatalogVisibility.Platform ||
      /* IHubCatalogVisibility.Global || */
      IHubCatalogVisibility.Company:
      return "success";
    case IHubCatalogVisibility.Private:
      return "info";
    default:
      return "neutral";
  }
}

function VisibilitySelectCell({
  row,
  value,
  visibilitySelectOptions,
  isUpdatingVisibility,
  requestVisibilityChange,
  t,
}: {
  row: IHubCatalog;
  value: IHubCatalogVisibility;
  visibilitySelectOptions: {
    value: IHubCatalogVisibility;
    label: string;
    description: string;
  }[];
  isUpdatingVisibility: boolean;
  requestVisibilityChange: (
    row: IHubCatalog,
    next: IHubCatalogVisibility,
  ) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const { control } = useForm<{ visibility: IHubCatalogVisibility }>({
    defaultValues: { visibility: value },
    values: { visibility: value },
  });

  return (
    <FormSelectField<{ visibility: IHubCatalogVisibility }>
      control={control}
      name="visibility"
      items={visibilitySelectOptions}
      disabled={
        isUpdatingVisibility || row.status !== IHubCatalogStatus.Approved
      }
      placeholder={t("hub.catalog_manager.visibility_placeholder", {
        defaultValue: "Visibility",
      })}
      onValueChange={(v) => {
        requestVisibilityChange(row, v as IHubCatalogVisibility);
      }}
    />
  );
}

export function Reviews() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const suppressRowClickUntilMs = useRef(0);
  const [visibilityConfirm, setVisibilityConfirm] =
    useState<VisibilityChangeConfirm | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { data: catalogsData, isLoading: catalogsLoading } =
    useSearchHubCatalogs({
      filter: new FilterBuilder()
        .in("status", [
          IHubCatalogStatus.Review,
          IHubCatalogStatus.Rejected,
          IHubCatalogStatus.Approved,
        ])
        .contains("label", searchQuery.trim())
        .build(),
      sort: ["status ASC", "createdByDatetime DESC"],
      mode: "DATA",
      startIndex: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    });
  const { isPermission: hasCatalogReviewParticipate } = usePermission([
    HUB_PERM.CATALOG_REVIEW_PARTICIPATE,
  ]);
  const { isPermission: hasCatalogReviewView } = usePermission([
    HUB_PERM.CATALOG_REVIEW_VIEW,
  ]);
  const { data: reviewsData } = useSearchHubReviews({
    filter: new FilterBuilder().build(),
    sort: ["status ASC", "reviewedDatetime DESC"],
    mode: "DATA",
  });
  const reviews = useMemo(
    () =>
      reviewsData?.pages.flatMap(
        (page: { items: IHubReview[] }) => page.items,
      ) ?? [],
    [reviewsData],
  );
  const { mutate: updateCatalog, isPending: isUpdatingVisibility } =
    useUpdateHubCatalog({
      onSuccess: () => {
        toast.add({
          title: t("hub.catalog_manager.reviews_visibility_updated", {
            defaultValue: "Visibility updated",
          }),
          description: t(
            "hub.catalog_manager.reviews_visibility_updated_description",
            {
              defaultValue: "The catalog visibility was saved.",
            },
          ),
          data: { type: "success" },
        });
      },
    });

  const visibilitySelectOptions = useMemo(
    () => [
      {
        value: IHubCatalogVisibility.Private,
        label: t("hub.catalog_manager.visibility_private", {
          defaultValue: "Private",
        }),
        description: t("hub.catalog_manager.visibility_private_desc", {
          defaultValue: "Only you can see this catalog.",
        }),
      },
      {
        value: IHubCatalogVisibility.Company,
        label: t("hub.catalog_manager.visibility_company", {
          defaultValue: "Company",
        }),
        description: t("hub.catalog_manager.visibility_company_desc", {
          defaultValue: "Anyone in your company can see this catalog.",
        }),
      },
      {
        value: IHubCatalogVisibility.Platform,
        label: t("hub.catalog_manager.visibility_platform", {
          defaultValue: "Platform",
        }),
        description: t("hub.catalog_manager.visibility_platform_desc", {
          defaultValue: "Visible to the platform.",
        }),
      },
      /* {
        value: IHubCatalogVisibility.Global,
        label: t("hub.catalog_manager.visibility_global", {
          defaultValue: "Global",
        }),
        description: t("hub.catalog_manager.visibility_global_desc", {
          defaultValue: "Anyone can see this catalog.",
        }),
      }, */
    ],
    [t],
  );

  const totalCount = useMemo(
    () => catalogsData?.pages[0]?.totalCount ?? 0,
    [catalogsData],
  );
  const { data: catalogTypes = [] } = useHubCatalogTypes({
    mode: "DATA",
    sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
    startIndex: 0,
    limit: 100,
  });

  const typeLabelByTypeId = useMemo(() => {
    const m = new Map<number, string>();
    for (const ct of catalogTypes as IHubCatalogType[]) {
      if (ct.typeId != null) {
        m.set(ct.typeId, ct.label || ct.name || String(ct.typeId));
      }
    }
    return m;
  }, [catalogTypes]);

  const pendingReviewByCatalogId = useMemo(() => {
    const m = new Map<number, IHubReview>();
    for (const r of reviews) {
      if (
        r.reviewId != null &&
        r.status === IHubReviewStatus.InReview &&
        !m.has(r.catalogId)
      ) {
        m.set(r.catalogId, r);
      }
    }
    return m;
  }, [reviews]);

  const catalogs = useMemo(() => {
    return (
      catalogsData?.pages.flatMap(
        (page: { items: IHubCatalog[] }) => page.items,
      ) ?? []
    );
  }, [catalogsData]);

  const openCatalogReview = useCallback(
    (row: IHubCatalog) => {
      const id = row.catalogId;
      const version = row.version;
      if (id == null || id <= 0) return;
      navigate(
        `/${Paths.CatalogManager}/${Paths.ReviewCatalog}?catalogId=${id}&version=${version}&tab=reviews`,
      );
    },
    [navigate],
  );

  const requestVisibilityChange = useCallback(
    (row: IHubCatalog, next: IHubCatalogVisibility) => {
      const current = row.visibility ?? IHubCatalogVisibility.Private;
      if (next === current) return;
      suppressRowClickUntilMs.current = Date.now() + ROW_CLICK_SUPPRESS_MS;
      setVisibilityConfirm({ catalog: row, next });
    },
    [],
  );

  const confirmVisibilityChange = useCallback(() => {
    if (visibilityConfirm == null) return;

    updateCatalog({
      ...visibilityConfirm.catalog,
      visibility: visibilityConfirm.next,
      status:
        visibilityConfirm.next === IHubCatalogVisibility.Private ||
        visibilityConfirm.next === IHubCatalogVisibility.Company
          ? IHubCatalogStatus.Submitted
          : IHubCatalogStatus.Review,
    });
    setVisibilityConfirm(null);
  }, [visibilityConfirm, updateCatalog]);

  const columns = useMemo<ColumnDef<IHubCatalog, unknown>[]>(() => {
    const cols: ColumnDef<IHubCatalog, unknown>[] = [
      {
        id: "catalogName",
        accessorFn: (row) => row.label || row.name || "—",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_manager.reviews_column_catalog_name", {
              defaultValue: "Catalog name",
            })}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) => {
          const value = String(info.getValue() ?? "—");
          return (
            <WithToolTip tooltipText={value}>
              <Label className="block min-w-0 truncate font-normal">
                {value}
              </Label>
            </WithToolTip>
          );
        },
      },
      {
        id: "version",
        accessorFn: (row) => row.version,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.home.solution_version_label", {
              defaultValue: "Version",
            })}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) => {
          const v = info.getValue() as number | undefined;
          return (
            <Label className="block min-w-0 truncate font-normal">
              {v != null ? String(v) : "—"}
            </Label>
          );
        },
      },
      {
        id: "catalogType",
        accessorFn: (row) => typeLabelByTypeId.get(row.typeId) ?? "—",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.breadcrumb.catalog_type", {
              defaultValue: "Catalog type",
            })}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) => (
          <Label className="block min-w-0 truncate font-normal">
            {String(info.getValue() ?? "—")}
          </Label>
        ),
      },
      {
        id: "submittedBy",
        accessorFn: (row) => {
          const pending =
            row.catalogId != null
              ? pendingReviewByCatalogId.get(row.catalogId)
              : undefined;
          return pending?.submittedBy?.trim() || row.createdBy?.trim() || "—";
        },
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_manager.reviews_column_submitted_by", {
              defaultValue: "Submitted by",
            })}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) => (
          <Label className="block min-w-0 truncate font-normal">
            {String(info.getValue() ?? "—")}
          </Label>
        ),
      },
      {
        id: "submittedOn",
        accessorFn: (row) => {
          const pending =
            row.catalogId != null
              ? pendingReviewByCatalogId.get(row.catalogId)
              : undefined;
          const iso =
            pending?.submittedDatetime ||
            row.modifiedByDatetime ||
            row.createdByDatetime;
          return formatSubmittedAt(iso);
        },
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_manager.reviews_column_submitted_on", {
              defaultValue: "Submitted on",
            })}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) =>
          String(info.getValue() ?? "—"),
      },
      {
        id: "status",
        accessorFn: (row) => row.status,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
          />
        ),
        cell: (info: CellContext<IHubCatalog, unknown>) =>
          getHubCatalogLifecycleBadge(
            info.row.original.status,
            t,
            undefined,
            info.row.original.visibility,
          ),
      },
      {
        id: "visibility",
        accessorFn: (row) => row.visibility ?? IHubCatalogVisibility.Private,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_manager.reviews_column_visibility", {
              defaultValue: "Visibility",
            })}
          />
        ),
        enableSorting: false,
        cell: (info: CellContext<IHubCatalog, unknown>) => {
          const row = info.row.original;
          const id = row.catalogId;
          const value = row.visibility ?? IHubCatalogVisibility.Private;
          const hasCatalogId = id != null && id > 0;
          const visibilityLabel =
            visibilitySelectOptions.find((o) => o.value === value)?.label ??
            String(value);

          if (!hasCatalogId) {
            return (
              <Label className="text-muted-foreground font-normal">—</Label>
            );
          }

          if (hasCatalogReviewParticipate) {
            return (
              <VisibilitySelectCell
                row={row}
                value={value}
                visibilitySelectOptions={visibilitySelectOptions}
                isUpdatingVisibility={isUpdatingVisibility}
                requestVisibilityChange={requestVisibilityChange}
                t={t}
              />
            );
          }

          if (hasCatalogReviewView) {
            return (
              <Badge
                size="sm"
                appearance="outline"
                dot
                variant={visibilityReadOnlyBadgeVariant(value)}>
                {visibilityLabel}
              </Badge>
            );
          }

          return <Label className="text-muted-foreground font-normal">—</Label>;
        },
      },
    ];

    if (hasCatalogReviewParticipate) {
      cols.push({
        id: "actions",
        header: t("hub.catalog_manager.reviews_column_actions", {
          defaultValue: "Actions",
        }),
        enableSorting: false,
        cell: (info: CellContext<IHubCatalog, unknown>) => {
          const row = info.row.original;
          if (!canShowCatalogReviewAction(row.status)) {
            return null;
          }
          return (
            <ToolbarGroup className="gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openCatalogReview(row);
                }}>
                {t("hub.catalog_manager.reviews", {
                  defaultValue: "Review",
                })}
              </Button>
            </ToolbarGroup>
          );
        },
      });
    }

    return cols;
  }, [
    t,

    typeLabelByTypeId,
    pendingReviewByCatalogId,
    openCatalogReview,
    hasCatalogReviewParticipate,
    hasCatalogReviewView,
    visibilitySelectOptions,
    isUpdatingVisibility,
    requestVisibilityChange,
  ]);

  return (
    <WithPermission permissions={[HUB_PERM.CATALOG_REVIEW_VIEW]}>
      <ConfirmAction
        open={visibilityConfirm != null}
        onOpenChange={(open) => {
          if (!open) setVisibilityConfirm(null);
        }}
        title={t("hub.catalog_manager.reviews_visibility_confirm_title", {
          defaultValue: "Change catalog visibility?",
        })}
        description={
          visibilityConfirm != null
            ? t("hub.catalog_manager.reviews_visibility_confirm_description", {
                name: visibilityConfirm.catalog.label,
                current: visibilityLabelForValue(
                  visibilityConfirm.catalog.visibility ??
                    IHubCatalogVisibility.Private,
                  visibilitySelectOptions,
                ),
                next: visibilityLabelForValue(
                  visibilityConfirm.next,
                  visibilitySelectOptions,
                ),
                defaultValue:
                  'Change visibility for "{{name}}" from {{current}} to {{next}}? This updates who can see the catalog in the explorer.',
              })
            : null
        }
        cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
        confirmText={t(
          "hub.catalog_manager.reviews_visibility_confirm_action",
          {
            defaultValue: "Change visibility",
          },
        )}
        onConfirm={confirmVisibilityChange}
        disabled={isUpdatingVisibility}
      />
      <DataGrid<IHubCatalog>
        data={catalogs}
        columns={columns}
        recordCount={totalCount}
        tableLayout={DATA_GRID_LAYOUT}
        paginationPlacement="toolbar"
        paginationConfig={{ showRowsPerPage: true }}
        manualPagination
        pageCount={Math.ceil(totalCount / pagination.pageSize)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={catalogsLoading}
        loadingMessage={t("hub.catalog_manager.reviews_loading", {
          defaultValue: "Loading reviews…",
        })}
        withSearch={{
          searchKey: "label",
          placeholder: t("hub.catalog_manager.search_placeholder", {
            defaultValue: "Search...",
          }),
          onChange: setSearchQuery,
        }}
        emptyMessage={
          catalogs.length === 0
            ? t("hub.catalog_manager.reviews_empty")
            : t("hub.catalog_manager.reviews_no_results", {
                defaultValue: "No catalogs match your search.",
              })
        }
      />
    </WithPermission>
  );
}
